import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  FileText,
  FolderOpen,
  Blocks,
  Layout as LayoutIcon,
  Settings,
  LogOut,
  User,
  Menu,
  X,
  Users,
  FileCheck
} from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { user, isAdmin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Verträge', href: '/contracts', icon: FileText },
    { name: 'Vorlagen', href: '/templates', icon: FolderOpen },
    { name: 'Signierte Dokumente', href: '/signed-documents', icon: FileCheck },
  ];

  const adminNavigation = [
    { name: 'Bauteile', href: '/admin/blocks', icon: Blocks },
    { name: 'Abteile', href: '/admin/sections', icon: LayoutIcon },
    { name: 'Benutzer', href: '/admin/users', icon: Users },
    { name: 'Einstellungen', href: '/admin/settings', icon: Settings },
  ];

  const isActive = (path: string) => location.pathname.startsWith(path);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo und Navigation */}
            <div className="flex min-w-0">
              <Link to="/" className="flex items-center flex-shrink-0">
                <FileText className="h-8 w-8 text-primary-600" />
                <span className="ml-2 text-xl font-bold text-gray-900">
                  VertragsSystem
                </span>
              </Link>

              {/* Desktop Navigation */}
              <nav className="hidden 2xl:ml-8 2xl:flex 2xl:space-x-4 min-w-0 overflow-x-auto">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`inline-flex items-center whitespace-nowrap px-3 py-2 text-sm font-medium rounded-md ${
                      isActive(item.href)
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <item.icon className="h-4 w-4 mr-2" />
                    {item.name}
                  </Link>
                ))}

                {isAdmin && (
                  <>
                    <div className="border-l border-gray-200 mx-2" />
                    {adminNavigation.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`inline-flex items-center whitespace-nowrap px-3 py-2 text-sm font-medium rounded-md ${
                          isActive(item.href)
                            ? 'bg-primary-50 text-primary-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <item.icon className="h-4 w-4 mr-2" />
                        {item.name}
                      </Link>
                    ))}
                  </>
                )}
              </nav>
            </div>

            {/* User Menu */}
            <div className="hidden 2xl:flex 2xl:items-center 2xl:space-x-4 flex-shrink-0 ml-4">
              <div className="flex items-center text-sm text-gray-600">
                <User className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="whitespace-nowrap">{user?.firstName} {user?.lastName}</span>
                {isAdmin && (
                  <span className="ml-2 px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">
                    Admin
                  </span>
                )}
              </div>
              <button
                onClick={handleLogout}
                className="inline-flex items-center whitespace-nowrap flex-shrink-0 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Abmelden
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="flex items-center flex-shrink-0 ml-4 2xl:hidden">
              <button
                onClick={handleLogout}
                title="Abmelden"
                className="inline-flex items-center whitespace-nowrap px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md"
              >
                <LogOut className="h-5 w-5 sm:mr-2" />
                <span className="hidden sm:inline">Abmelden</span>
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="2xl:hidden border-t border-gray-200">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center px-3 py-2 text-base font-medium rounded-md ${
                    isActive(item.href)
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <item.icon className="h-5 w-5 mr-3" />
                  {item.name}
                </Link>
              ))}

              {isAdmin && (
                <>
                  <div className="border-t border-gray-200 my-2" />
                  <div className="px-3 py-2 text-xs font-semibold text-gray-400 uppercase">
                    Administration
                  </div>
                  {adminNavigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center px-3 py-2 text-base font-medium rounded-md ${
                        isActive(item.href)
                          ? 'bg-primary-50 text-primary-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <item.icon className="h-5 w-5 mr-3" />
                      {item.name}
                    </Link>
                  ))}
                </>
              )}

              <div className="border-t border-gray-200 my-2" />
              <div className="px-3 py-2 text-sm text-gray-600">
                <User className="h-4 w-4 inline mr-2" />
                {user?.firstName} {user?.lastName}
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-3 py-2 text-base font-medium text-gray-600 hover:bg-gray-50 rounded-md"
              >
                <LogOut className="h-5 w-5 mr-3" />
                Abmelden
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
}
